/**
 * Token Refresh Middleware
 *
 * Keeps the session alive by refreshing the access token when needed, and retries
 * API requests that failed due to an expired or invalid token (401).
 *
 * Two refresh strategies:
 *
 * 1. PROACTIVE (before the request)
 *    If the access token is already expired according to localStorage (e.g. expiry
 *    time + buffer), we postpone the RSAA, call the refresh endpoint, then replay
 *    all postponed requests after a successful refresh.
 *
 * 2. REACTIVE (after a 401 response)
 *    If the server returns 401 (e.g. token expired on server, clock skew), we
 *    intercept the FAILURE action, postpone that RSAA, trigger refresh, and replay
 *    it after success. The FAILURE is suppressed so the UI doesn't see the error.
 *    If the replay still returns 401 (e.g. IP restriction — auth refresh can succeed
 *    while platform APIs reject), we log out instead of refreshing again.
 *
 * Important: redux-api-middleware emits FAILURE by calling the `next` we pass it,
 * so FAILURE never goes through our main (action) => handler. We must pass a
 * wrapper as `next` so we see REQUEST / SUCCESS / FAILURE and can react to 401.
 */
import { isRSAA, apiMiddleware, RSAA } from 'redux-api-middleware';
import {
  isAccessTokenExpired,
  refreshAccessToken,
  TOKEN_RECEIVED,
  TOKEN_FAILURE,
  logoutUser,
  updateAccessTokenExpirationTime,
  getCsrfToken,
  CSRF_TOKEN_HEADER_KEY,
  updateCsrfToken,
  getLoggedInUser,
  getDeviceKey,
} from 'services/AuthService';

/**
 * Returns true if the action is an RSAA FAILURE with HTTP status 401.
 * Used to trigger reactive refresh (server says token is invalid).
 */
function is401Failure(action) {
  return (
    action != null &&
    typeof action === 'object' &&
    action.error === true &&
    action.payload != null &&
    action.payload.status === 401
  );
}

export function createTokenRefreshMiddleware() {
  /** RSAA actions waiting for a successful token refresh; replayed after TOKEN_RECEIVED */
  let postponedRSAAs = [];

  return ({ dispatch, getState }) => {
    const rsaaMiddleware = apiMiddleware({ dispatch, getState });

    return (next) => (action) => {
      /**
       * Handler used when we trigger a refresh (proactive or reactive).
       * Receives TOKEN_REQUEST / TOKEN_RECEIVED / TOKEN_FAILURE from the refresh call.
       * On TOKEN_RECEIVED we update localStorage and replay all postponed RSAA actions.
       */
      const dispatchPostponedActions = (nextAction) => {
        if (nextAction.type === TOKEN_RECEIVED) {
          next(nextAction);
          const { currentTimeMillis, idTokenDurationSecs, csrfToken } = nextAction.payload;
          updateAccessTokenExpirationTime(currentTimeMillis, idTokenDurationSecs);
          // Support csrf token in the refresh token response. Currently backend is not sending csrf token in the response.
          if (csrfToken) {
            updateCsrfToken(csrfToken);
          }
          postponedRSAAs.forEach((postponed) => {
            postponed[RSAA].headers = postponed[RSAA].headers ?? {};
            postponed[RSAA].headers[CSRF_TOKEN_HEADER_KEY] = getCsrfToken();
            // Replays after refresh: a further 401 is not token expiry (e.g. IP block).
            rsaaMiddleware(createNextWith401Interceptor(next, postponed, true))(postponed);
          });
          postponedRSAAs = [];
        } else if (nextAction.type === TOKEN_FAILURE) {
          logoutUser();
        } else {
          next(nextAction);
        }
      };

      /**
       * Wraps the chain's `next` so we see every action the apiMiddleware emits
       * (REQUEST, SUCCESS, FAILURE) for a given RSAA. FAILURE actions are not
       * re-dispatched through our middleware, so we only see them in this wrapper.
       *
       * When we see a 401 FAILURE we:
       * - Push the original `rsaaAction` into postponedRSAAs
       * - Trigger refresh (once) and suppress the FAILURE
       * - After TOKEN_RECEIVED, the postponed action is replayed
       *
       * @param chainNext - The real next middleware (to pass non-401 actions through)
       * @param rsaaAction - The RSAA action we passed to the apiMiddleware (for retry on 401)
       * @param alreadyRetriedAfterRefresh - If true, do not refresh again on 401
       */
      function createNextWith401Interceptor(chainNext, rsaaAction, alreadyRetriedAfterRefresh = false) {
        return (outgoingAction) => {
          if (
            outgoingAction != null &&
            typeof outgoingAction === 'object' &&
            !isRSAA(outgoingAction) &&
            is401Failure(outgoingAction)
          ) {
            if (
              alreadyRetriedAfterRefresh ||
              outgoingAction.type === TOKEN_FAILURE ||
              !getLoggedInUser() ||
              !getDeviceKey()
            ) {
              logoutUser();
              return chainNext(outgoingAction);
            }
            postponedRSAAs.push(rsaaAction);
            if (postponedRSAAs.length === 1) {
              return rsaaMiddleware(dispatchPostponedActions)(refreshAccessToken());
            }
            return; // suppress 401; request will be retried after refresh
          }
          return chainNext(outgoingAction);
        };
      }

      // --- Only handle RSAA (API) actions; pass everything else through ---
      if (isRSAA(action)) {
        action[RSAA].credentials = action[RSAA].credentials ?? 'include';
        action[RSAA].headers = action[RSAA].headers ?? {};
        action[RSAA].headers[CSRF_TOKEN_HEADER_KEY] = getCsrfToken();

        // PROACTIVE: token already expired by time → refresh first, then run this request
        if (isAccessTokenExpired()) {
          // there is a need to refresh auth token
          // start holding RSAA actions in postponedRSAAs
          postponedRSAAs.push(action);

          // whenever we see first RSAA action,
          // send request to refreshToken
          // all subsequent RSAAs will be held
          // in postponedRSAAs until token is
          // refreshed
          if (postponedRSAAs.length === 1) {
            return rsaaMiddleware(dispatchPostponedActions)(refreshAccessToken());
          }
          return;
        }

        // Run the request; use wrapper so we see FAILURE (e.g. 401) and can react
        return rsaaMiddleware(createNextWith401Interceptor(next, action))(action);
      }

      return next(action);
    };
  };
}

export default createTokenRefreshMiddleware();
